<script setup>
import {computed} from "vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import _ from "lodash";

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  lastPage: {
    type: Number,
    required: true,
  },
  maxButtons: {
    type: Number,
    default: 7,
  }
});
const emit = defineEmits(['change'])

const pages = computed(() => {
  // Build an array of pages
  const pages = [...Array(props.lastPage).keys()].map(p => p + 1);

  if (pages.length <= props.maxButtons) {
    return pages;
  }

  // Subtract fixed first and last page buttons
  let pagesRemaining = props.maxButtons - 2;

  // Work out how many buttons either side of the current page we can show
  let bufferButtonsCount = Math.floor((pagesRemaining - 1) / 2);

  const currentPageIndex = pages.findIndex((page) => page === props.currentPage);

  let startIndex = Math.max(0, currentPageIndex - bufferButtonsCount);

  let endIndex = Math.min(pages.length - 1, startIndex + pagesRemaining);

  // Fix when at the end of pages and we want to show more prev pages
  if (endIndex - startIndex < pagesRemaining) {
    startIndex = endIndex - pagesRemaining;
  }

  // Fix to show correct number of pages when starting range includes page 1
  if (startIndex === 0) {
    endIndex++;
  }

  // Account for ellipsis at the start which takes a button space
  if (pages[startIndex] - 1 > 1) {
    startIndex++
  }

  // Account for ellipsis at the end which takes a button space
  if (props.lastPage - pages[endIndex] > 0) {
    endIndex--;
  }

  // Add first and last page and sort the range of pages
  return _.uniq(_.orderBy([
    1,
    ...pages.slice(startIndex, endIndex),
    props.lastPage,
  ]));
});
const hasNextButton = (page) => pages.value.find(p => p === page + 1) !== undefined;

const goToNext = () => {
  const page = props.currentPage + 1;
  if (page > props.lastPage) return

  emit('change', page)
}

const goToPrev = () => {
  const page = props.currentPage - 1;
  if (page < 1) return

  emit('change', page)
}

</script>

<template>
  <div
    v-show="pages.length > 1"
    class="tw-flex tw-gap-1 tw-items-center"
  >
    <DefaultButton
      :disabled="currentPage === 1"
      size="small"
      :class="[
        'tw-w-10 tw-h-10 !tw-px-3 tw-flex-0 !tw-border tw-shadow-light-blue-glow',
        {
          'tw-opacity-0' : currentPage === 1,
        }
      ]"
      variant="primary-inverted"
      @click="goToPrev"
    >
      <svg-vue
        icon="icons/chevron"
        class="tw-h-2 tw-w-2 tw-rotate-90"
      />
    </DefaultButton>
    <div
      v-for="page in pages"
      :key="page"
      class="tw-flex tw-items-end tw-gap-1"
    >
      <DefaultButton
        size="small"
        variant="base"
        :class="[
          '!tw-w-10 tw-h-10 !tw-text-base tw-flex-0 tw-text-center',
          {
            'tw-bg-transparent hover:!tw-bg-blue-highlight': page !== currentPage,
            '!tw-bg-blue tw-text-white tw-border-0': page === currentPage,
          },
        ]"
        @click="() => $emit('change', page)"
      >
        <span class="tw-w-3">{{ page }}</span>
      </DefaultButton>
      <div
        v-if="page < lastPage && !hasNextButton(page)"
        class="tw-font-bold tw-px-2"
      >
        ...
      </div>
    </div>
    <DefaultButton
      :disabled="currentPage === lastPage"
      size="small"
      :class="[
        'tw-w-10 tw-h-10 !tw-px-3 tw-flex-0 !tw-border tw-shadow-light-blue-glow',
        {
          'tw-opacity-0' : currentPage === lastPage,
        }
      ]"
      variant="primary-inverted"
      @click="goToNext"
    >
      <svg-vue
        icon="icons/chevron-thick"
        class="tw-h-3 tw-w-3 -tw-rotate-90"
      />
    </DefaultButton>
  </div>
</template>
